/**
 * useCloudImport Hook
 * 管理云端导入的完整逻辑
 *
 * 按照 3.0-phase-2-import-plan.md Section 六 设计：
 * - 获取本地数据结构（IPC）
 * - 获取预览数据（IPC）
 * - 创建导入任务（API）
 * - 执行导入（API: batch-items, upload-media）
 * - 轮询状态
 * - 暂停/恢复/取消
 *
 * 用户确认的设计决策：
 * - 帖子少 (≤15) 同步，帖子多异步
 * - 冲突处理：用户可选 "更新式上传" 选项（默认关闭=跳过已存在）
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { message } from 'antd';
import {
  createImportTask,
  startImportTask,
  pauseImportTask,
  resumeImportTask,
  cancelImportTask,
  getImportTaskStatus,
  batchImportItems,
  uploadImportMedia,
} from '../apiClient';
import type {
  CloudImportTask,
  CloudImportProgress,
  CloudImportConflictStrategy,
  CloudImportUploadMode,
  CloudImportIdMappings,
  CloudImportPreviewStats,
  LocalTagGroupForImport,
  LocalTagForImport,
  LocalStructureResponse,
} from '../types';

// 同步上传阈值（参考 Section 6.2）
const SYNC_THRESHOLD = 15;

// 轮询间隔（毫秒）
const POLL_INTERVAL = 2000;

// 批量导入大小
const BATCH_SIZE = 10;

export interface UseCloudImportOptions {
  /** 是否为 Electron 环境 */
  isElectron?: boolean;
  /** IPC 调用函数（Electron 专用） */
  ipcInvoke?: <T>(channel: string, ...args: any[]) => Promise<T>;
  /** 导入完成回调 */
  onComplete?: () => void;
}

export interface UseCloudImportReturn {
  // 状态
  /** 是否正在加载本地数据 */
  loadingLocalData: boolean;
  /** 本地数据结构 */
  localStructure: LocalStructureResponse | null;
  /** 当前导入任务 */
  currentTask: CloudImportTask | null;
  /** 当前进度 */
  currentProgress: CloudImportProgress | null;
  /** 预览统计 */
  previewStats: CloudImportPreviewStats | null;
  /** 是否正在导入 */
  isImporting: boolean;
  /** 错误信息 */
  error: string | null;

  // 操作
  /** 获取本地数据结构 */
  fetchLocalStructure: () => Promise<void>;
  /** 获取预览数据 */
  fetchPreview: (selectedTagGroupIds: string[], selectedTagIds: string[], includeUncategorized?: boolean) => Promise<{
    tagGroups: any[];
    tags: any[];
    bloggers: any[];
    posts: any[];
  } | null>;
  /** 开始导入 */
  startImport: (options: {
    selectedTagGroupIds: string[];
    selectedTagIds: string[];
    includeUncategorized?: boolean;
    conflictStrategy: CloudImportConflictStrategy;
    uploadMode?: CloudImportUploadMode;
  }) => Promise<void>;
  /** 暂停导入 */
  pauseImport: () => Promise<void>;
  /** 恢复导入 */
  resumeImport: () => Promise<void>;
  /** 取消导入 */
  cancelImport: () => Promise<void>;
  /** 关闭/清除当前任务 */
  clearTask: () => void;
  /** 建议的上传模式 */
  suggestedUploadMode: CloudImportUploadMode;
}

export const useCloudImport = (options: UseCloudImportOptions = {}): UseCloudImportReturn => {
  const { isElectron = false, ipcInvoke, onComplete } = options;

  // 状态
  const [loadingLocalData, setLoadingLocalData] = useState(false);
  const [localStructure, setLocalStructure] = useState<LocalStructureResponse | null>(null);
  const [currentTask, setCurrentTask] = useState<CloudImportTask | null>(null);
  const [currentProgress, setCurrentProgress] = useState<CloudImportProgress | null>(null);
  const [previewStats, setPreviewStats] = useState<CloudImportPreviewStats | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestedUploadMode, setSuggestedUploadMode] = useState<CloudImportUploadMode>('SYNC');

  // Refs
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const previewDataRef = useRef<{
    tagGroups: any[];
    tags: any[];
    bloggers: any[];
    posts: any[];
  } | null>(null);

  // 清理轮询
  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  // 清理
  useEffect(() => {
    return () => {
      stopPolling();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [stopPolling]);

  /**
   * 获取本地数据结构（Electron IPC）
   */
  const fetchLocalStructure = useCallback(async () => {
    if (!isElectron || !ipcInvoke) {
      setError('仅支持 Electron 端导入');
      return;
    }

    setLoadingLocalData(true);
    setError(null);

    try {
      const result = await ipcInvoke<{
        success: boolean;
        data?: LocalStructureResponse;
        message?: string;
      }>('cloud-import:get-local-structure');

      if (result.success && result.data) {
        setLocalStructure(result.data);
      } else {
        setError(result.message || '获取本地数据失败');
      }
    } catch (err: any) {
      console.error('[useCloudImport] Failed to fetch local structure:', err);
      setError(err?.message || '获取本地数据失败');
    } finally {
      setLoadingLocalData(false);
    }
  }, [isElectron, ipcInvoke]);

  /**
   * 获取预览数据（Electron IPC）
   */
  const fetchPreview = useCallback(
    async (selectedTagGroupIds: string[], selectedTagIds: string[], includeUncategorized?: boolean) => {
      if (!isElectron || !ipcInvoke) {
        setError('仅支持 Electron 端导入');
        return null;
      }

      try {
        const result = await ipcInvoke<{
          success: boolean;
          data?: {
            tagGroups: any[];
            tags: any[];
            bloggers: any[];
            posts: any[];
            stats: CloudImportPreviewStats;
          };
          message?: string;
        }>('cloud-import:get-preview', { selectedTagGroupIds, selectedTagIds, includeUncategorized });

        if (!result.success || !result.data) {
          setError(result.message || '获取预览数据失败');
          return null;
        }

        const { tagGroups, tags, bloggers, posts, stats } = result.data;

        // 保存预览数据供后续使用
        previewDataRef.current = {
          tagGroups,
          tags,
          bloggers,
          posts,
        };

        // 设置预览统计
        setPreviewStats(stats);

        // 根据帖子数量建议上传模式
        const mode = posts.length <= SYNC_THRESHOLD ? 'SYNC' : 'ASYNC';
        setSuggestedUploadMode(mode);

        return previewDataRef.current;
      } catch (err: any) {
        console.error('[useCloudImport] Failed to fetch preview:', err);
        setError(err?.message || '获取预览数据失败');
        return null;
      }
    },
    [isElectron, ipcInvoke]
  );

  /**
   * 轮询任务状态
   */
  const startPolling = useCallback((taskId: string) => {
    stopPolling();

    const poll = async () => {
      try {
        const result = await getImportTaskStatus(taskId);
        setCurrentTask(result.task);
        setCurrentProgress(result.progress);

        // 检查是否完成
        if (
          result.task.status === 'COMPLETED' ||
          result.task.status === 'FAILED' ||
          result.task.status === 'CANCELLED'
        ) {
          stopPolling();
          setIsImporting(false);

          if (result.task.status === 'COMPLETED') {
            message.success('导入完成！');
            onComplete?.();
          } else if (result.task.status === 'FAILED') {
            message.error('导入失败');
          }
        }
      } catch (err) {
        console.error('[useCloudImport] Poll error:', err);
      }
    };

    // 立即执行一次
    poll();

    // 开始轮询
    pollTimerRef.current = setInterval(poll, POLL_INTERVAL);
  }, [stopPolling, onComplete]);

  /**
   * 执行批量导入（核心导入逻辑）
   */
  const executeBatchImport = useCallback(
    async (
      taskId: string,
      preview: { tagGroups: any[]; tags: any[]; bloggers: any[]; posts: any[] },
      uploadMode: CloudImportUploadMode
    ) => {
      const idMappings: CloudImportIdMappings = {
        tagGroups: {},
        tags: {},
        bloggers: {},
        posts: {},
      };

      // 按顺序导入：标签组 → 标签 → 博主 → 帖子（参考 Section 6.1 step 8）

      // 1. 导入标签组
      if (preview.tagGroups.length > 0) {
        const items = preview.tagGroups.map((tg) => ({ type: 'TAG_GROUP' as const, data: tg }));
        for (let i = 0; i < items.length; i += BATCH_SIZE) {
          const batch = items.slice(i, i + BATCH_SIZE);
          const result = await batchImportItems(taskId, { items: batch, idMappings });
          Object.assign(idMappings.tagGroups, result.idMappings.tagGroups);
        }
      }

      // 2. 导入标签（包括一级标签 + 标签组下的子标签）
      // 提取标签组下的子标签
      const groupedTags = preview.tagGroups.flatMap((tg) => tg.tags || []);
      const allTags = [...preview.tags, ...groupedTags];

      if (allTags.length > 0) {
        const items = allTags.map((t) => ({ type: 'TAG' as const, data: t }));
        for (let i = 0; i < items.length; i += BATCH_SIZE) {
          const batch = items.slice(i, i + BATCH_SIZE);
          const result = await batchImportItems(taskId, { items: batch, idMappings });
          Object.assign(idMappings.tags, result.idMappings.tags);
        }
      }

      // 3. 导入博主
      if (preview.bloggers.length > 0) {
        const items = preview.bloggers.map((b) => ({ type: 'BLOGGER' as const, data: b }));
        for (let i = 0; i < items.length; i += BATCH_SIZE) {
          const batch = items.slice(i, i + BATCH_SIZE);
          const result = await batchImportItems(taskId, { items: batch, idMappings });
          Object.assign(idMappings.bloggers, result.idMappings.bloggers);
        }
      }

      // 4. 导入帖子
      if (preview.posts.length > 0) {
        const items = preview.posts.map((p) => ({ type: 'POST' as const, data: p }));
        for (let i = 0; i < items.length; i += BATCH_SIZE) {
          const batch = items.slice(i, i + BATCH_SIZE);
          const result = await batchImportItems(taskId, { items: batch, idMappings });
          Object.assign(idMappings.posts, result.idMappings.posts);

          // 同步模式：立即上传媒体
          if (uploadMode === 'SYNC' && ipcInvoke) {
            for (const item of batch) {
              const cloudPostId = idMappings.posts[item.data.id];
              if (cloudPostId) {
                try {
                  // 读取本地媒体
                  const mediaResult = await ipcInvoke<{
                    success: boolean;
                    data?: {
                      images: string[];
                      video: string | null;
                      hasLocalImages: boolean;
                      hasLocalVideo: boolean;
                    };
                    message?: string;
                  }>('cloud-import:read-post-media', item.data.id);

                  // 检查 IPC 调用是否成功
                  if (!mediaResult.success || !mediaResult.data) {
                    console.warn(`[useCloudImport] Failed to read media for post ${item.data.id}:`, mediaResult.message);
                    continue;
                  }

                  const { images, video } = mediaResult.data;

                  // 上传到云端
                  if (images.length > 0 || video) {
                    await uploadImportMedia(taskId, {
                      postId: cloudPostId,
                      images: images.length > 0 ? images : undefined,
                      video: video || undefined,
                    });
                  }
                } catch (mediaErr) {
                  console.error(`[useCloudImport] Failed to upload media for post ${item.data.id}:`, mediaErr);
                }
              }
            }
          }
        }
      }

      return idMappings;
    },
    [ipcInvoke]
  );

  /**
   * 开始导入
   */
  const startImport = useCallback(
    async (importOptions: {
      selectedTagGroupIds: string[];
      selectedTagIds: string[];
      includeUncategorized?: boolean;
      conflictStrategy: CloudImportConflictStrategy;
      uploadMode?: CloudImportUploadMode;
    }) => {
      if (!isElectron || !ipcInvoke) {
        setError('仅支持 Electron 端导入');
        return;
      }

      // 获取预览数据（如果还没有）
      let preview = previewDataRef.current;
      if (!preview) {
        preview = await fetchPreview(
          importOptions.selectedTagGroupIds,
          importOptions.selectedTagIds,
          importOptions.includeUncategorized
        );
        if (!preview) {
          return;
        }
      }

      setIsImporting(true);
      setError(null);

      try {
        // 1. 创建导入任务
        const createResult = await createImportTask({
          selectedTagIds: importOptions.selectedTagIds,
          selectedTagGroupIds: importOptions.selectedTagGroupIds,
          conflictStrategy: importOptions.conflictStrategy,
          preview,
        });

        const task = createResult.task;
        const uploadMode = importOptions.uploadMode ?? createResult.suggestedUploadMode;
        setCurrentTask(task);

        // 2. 开始任务
        await startImportTask(task.id);

        // 3. 开始轮询
        startPolling(task.id);

        // 4. 执行批量导入
        await executeBatchImport(task.id, preview, uploadMode);

        // 清理预览数据
        previewDataRef.current = null;
      } catch (err: any) {
        console.error('[useCloudImport] Import failed:', err);
        setError(err?.message || '导入失败');
        setIsImporting(false);
        message.error(err?.message || '导入失败');
      }
    },
    [isElectron, ipcInvoke, fetchPreview, startPolling, executeBatchImport]
  );

  /**
   * 暂停导入
   */
  const pauseImport = useCallback(async () => {
    if (!currentTask) return;

    try {
      await pauseImportTask(currentTask.id);
      setCurrentTask((prev) => (prev ? { ...prev, status: 'PAUSED' } : null));
      stopPolling();
      message.info('导入已暂停');
    } catch (err: any) {
      console.error('[useCloudImport] Pause failed:', err);
      message.error('暂停失败');
    }
  }, [currentTask, stopPolling]);

  /**
   * 恢复导入
   */
  const resumeImport = useCallback(async () => {
    if (!currentTask) return;

    try {
      await resumeImportTask(currentTask.id);
      setCurrentTask((prev) => (prev ? { ...prev, status: 'RUNNING' } : null));
      startPolling(currentTask.id);
      message.info('导入已恢复');
    } catch (err: any) {
      console.error('[useCloudImport] Resume failed:', err);
      message.error('恢复失败');
    }
  }, [currentTask, startPolling]);

  /**
   * 取消导入
   */
  const cancelImport = useCallback(async () => {
    if (!currentTask) return;

    try {
      await cancelImportTask(currentTask.id);
      setCurrentTask((prev) => (prev ? { ...prev, status: 'CANCELLED' } : null));
      stopPolling();
      setIsImporting(false);
      message.info('导入已取消');
    } catch (err: any) {
      console.error('[useCloudImport] Cancel failed:', err);
      message.error('取消失败');
    }
  }, [currentTask, stopPolling]);

  /**
   * 清除当前任务
   */
  const clearTask = useCallback(() => {
    stopPolling();
    setCurrentTask(null);
    setCurrentProgress(null);
    setPreviewStats(null);
    setIsImporting(false);
    setError(null);
    previewDataRef.current = null;
  }, [stopPolling]);

  return {
    // 状态
    loadingLocalData,
    localStructure,
    currentTask,
    currentProgress,
    previewStats,
    isImporting,
    error,

    // 操作
    fetchLocalStructure,
    fetchPreview,
    startImport,
    pauseImport,
    resumeImport,
    cancelImport,
    clearTask,
    suggestedUploadMode,
  };
};

export default useCloudImport;
